<template>
  <div
    class="mx-auto max-w-[var(--breakpoint-2xl)] px-4 pt-12 sm:px-8 sm:pt-16"
  >
    <div class="relative isolate h-full w-full rounded-2xl">
      <div
        class="absolute inset-0 h-full w-full animate-pulse rounded-2xl bg-yellow-400/40 blur-lg"
        style="animation-duration: 5s"
      />
      <UCard
        :ui="{
          root: 'z-[1] relative ring-yellow-400 ring-2 isolate bg-neutral-950 backdrop-blur-lg overflow-hidden',
          header: 'relative z-[2]',
          body: 'border-none mb-2',
          footer: 'relative z-[2] border-t border-neutral-800',
        }"
      >
        <template #header>
          <div class="flex items-center justify-between">
            <div>
              <p class="font-bold text-yellow-400 uppercase">Book Now</p>
              <h1 class="text-2xl font-extrabold drop-shadow-lg">
                Your Next Adventure Is Just A Few Clicks Away
              </h1>
            </div>
            <div
              class="h-10 w-10 shrink-0 rounded-full bg-neutral-900 p-2 ring-2 ring-yellow-400"
            >
              <UIcon name="i-lucide-sparkles" class="size-6 text-yellow-400" />
            </div>
          </div>
        </template>
        <div
          class="absolute inset-0 h-full w-full animate-pulse opacity-20"
          style="animation-duration: 5s"
        >
          <BrandTopography />
        </div>
        <div
          class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"
        />
        <div class="relative z-[2]">
          <!-- Are You Booking For A Group? -->
          <div v-if="groupBooking === null" class="space-y-6 text-center">
            <h2 class="text-xl font-bold text-yellow-400 drop-shadow-lg">
              Are You Booking For A Group?
            </h2>
            <div class="space-x-4">
              <UButton
                size="xl"
                color="primary"
                class="cursor-pointer"
                @click="groupBooking = true"
                >YES</UButton
              >
              <span class="font-bold text-white uppercase">OR</span>
              <UButton
                size="xl"
                color="primary"
                class="cursor-pointer"
                @click="groupBooking = false"
              >
                NO
              </UButton>
            </div>
          </div>

          <!-- What Type Of Group Are You Booking For? -->
          <div
            v-if="groupBooking === true && selectedGroup === null"
            class="space-y-6 text-center"
          >
            <h2 class="text-xl font-bold text-yellow-400 drop-shadow-lg">
              What Type Of Group Are You Booking For?
            </h2>
            <div
              class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
            >
              <LazyUCard
                v-for="(group, index) in groups"
                v-if="groups.length > 0 && !isLoading"
                :key="index"
                :ui="{
                  root: 'overflow-hidden group hover:ring-yellow-400 hover:ring-2 aspect-[2/1] sm:aspect-[3/1] md:aspect-[2/1] duration-300 cursor-pointer bg-black',
                  body: 'relative isolate w-full h-full',
                }"
                @click="selectGroup(group)"
              >
                <NuxtImg
                  provider="storyblok"
                  :modifiers="{ smart: true }"
                  class="absolute inset-0 z-[0] h-full w-full object-cover object-center opacity-60 duration-300 group-hover:scale-105 group-hover:opacity-100"
                  sizes="xs:100svw sm:50svw md:35svw lg:20svw xl:20svw xxl:20svw"
                  :src="group.content?.feature_image?.filename ?? ''"
                  :alt="group.content?.feature_image?.alt ?? ''"
                  loading="lazy"
                  placeholder
                />
                <div
                  class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/75 to-neutral-950 opacity-60 opacity-90 duration-300 group-hover:opacity-100"
                />
                <div
                  class="relative z-[2] flex h-full items-center justify-center text-center"
                >
                  <div class="relative flex flex-col items-center duration-300">
                    <h2
                      class="text-2xl font-extrabold drop-shadow duration-300 group-hover:text-yellow-400"
                    >
                      {{ group.name }}
                    </h2>
                  </div>
                </div>
              </LazyUCard>
              <LazyUCard
                v-for="i in 10"
                v-if="loading || isLoading"
                :key="i"
                :ui="{
                  root: 'overflow-hidden aspect-[2/1] sm:aspect-[3/1] md:aspect-[2/1]',
                  body: 'relative isolate w-full h-full',
                }"
              >
                <div
                  class="relative z-[2] flex h-full items-center justify-center text-center"
                >
                  <div class="relative flex w-full flex-col items-center">
                    <USkeleton class="h-8 w-2/3" />
                  </div>
                </div>
              </LazyUCard>
            </div>
          </div>

          <!-- Which Location Are You Booking At? (Group) -->
          <div v-if="selectedGroup !== null" class="space-y-6 text-center">
            <h2 class="text-xl font-bold text-yellow-400 drop-shadow-lg">
              Which Location Are You Booking At?
            </h2>
            <div
              :class="[
                'grid grid-cols-1 gap-4 sm:grid-cols-2',
                selectedGroup.content.locations.length === 1
                  ? 'sm:grid-cols-1'
                  : '',
                selectedGroup.content.locations.length === 2
                  ? 'sm:grid-cols-2'
                  : '',
                selectedGroup.content.locations.length >= 3
                  ? 'sm:grid-cols-2 lg:grid-cols-3'
                  : '',
              ]"
            >
              <LazyUCard
                v-for="(location, index) in selectedGroup.content.locations"
                v-if="selectedGroup !== null"
                :key="index"
                :ui="{
                  root: `overflow-hidden group ${
                    route.query.location
                      ?.toString()
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (letter) => letter.toUpperCase()) ===
                    location.name
                      ? 'ring-2 ring-yellow-400'
                      : ''
                  } hover:ring-yellow-400 hover:ring-2 aspect-[2/1] sm:aspect-[3/1] md:aspect-[2/1] duration-300 cursor-pointer bg-black`,
                  body: 'relative isolate w-full h-full',
                }"
                @click="
                  selectGroupLocation(selectedGroup.full_slug, location.name)
                "
              >
                <NuxtImg
                  provider="storyblok"
                  :modifiers="{ smart: true }"
                  class="absolute inset-0 z-[0] h-full w-full object-cover object-center opacity-60 duration-300 group-hover:scale-105 group-hover:opacity-100"
                  sizes="xs:100svw sm:100svw md:100svw lg:35svw xl:35svw xxl:25svw"
                  :src="location.content?.feature_image?.filename ?? ''"
                  :alt="location.content?.feature_image?.alt ?? ''"
                  loading="lazy"
                  placeholder
                />
                <div
                  class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/75 to-neutral-950 opacity-90 duration-300 group-hover:opacity-100"
                />
                <UBadge
                  v-if="
                    route.query.location
                      ?.toString()
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (letter) => letter.toUpperCase()) ===
                    location.name
                  "
                  size="xl"
                  class="absolute top-2 right-2 z-[3] rounded-full bg-neutral-900 p-0.5"
                >
                  <UIcon
                    name="i-lucide-circle-check"
                    class="size-5 shrink-0 text-yellow-400"
                  />
                </UBadge>
                <div
                  class="relative z-[2] flex h-full items-center justify-center text-center"
                >
                  <div class="relative flex flex-col items-center duration-300">
                    <h2
                      class="text-2xl font-extrabold drop-shadow duration-300 group-hover:text-yellow-400"
                    >
                      {{ location.name }}
                    </h2>
                    <p class="text-lg font-bold text-yellow-400 uppercase">
                      {{ location.content.town }}
                    </p>
                  </div>
                </div>
              </LazyUCard>
            </div>
          </div>

          <!-- Which Location Are You Booking At? (Non Group) -->
          <div
            v-if="groupBooking === false && selectedLocationName === null"
            class="space-y-6 text-center"
          >
            <h2 class="text-xl font-bold text-yellow-400 drop-shadow-lg">
              Which Location Are You Booking At?
            </h2>
            <div
              :class="[
                'grid grid-cols-1 gap-4 sm:grid-cols-2',
                locations.length === 1 ? 'sm:grid-cols-1' : '',
                locations.length === 2 ? 'sm:grid-cols-2' : '',
                locations.length >= 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : '',
                loading || isLoading ? 'sm:grid-cols-2 lg:grid-cols-3' : '',
              ]"
            >
              <LazyUCard
                v-for="(location, index) in locations"
                v-if="!loading && !isLoading"
                :key="index"
                :ui="{
                  root: `overflow-hidden group ${
                    route.query.location
                      ?.toString()
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (letter) => letter.toUpperCase()) ===
                    location.name
                      ? 'ring-2 ring-yellow-400'
                      : ''
                  } hover:ring-yellow-400 hover:ring-2 aspect-[2/1] sm:aspect-[3/1] md:aspect-[2/1] duration-300 cursor-pointer bg-black`,
                  body: 'relative isolate w-full h-full',
                }"
                @click="selectedLocation(location.name)"
              >
                <NuxtImg
                  provider="storyblok"
                  :modifiers="{ smart: true }"
                  class="absolute inset-0 z-[0] h-full w-full object-cover object-center opacity-60 duration-300 group-hover:scale-105 group-hover:opacity-100"
                  sizes="xs:100svw sm:100svw md:100svw lg:35svw xl:35svw xxl:25svw"
                  :src="location.content?.feature_image?.filename ?? ''"
                  :alt="location.content?.feature_image?.alt ?? ''"
                  loading="lazy"
                  placeholder
                />
                <div
                  class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/75 to-neutral-950 opacity-90 duration-300 group-hover:opacity-100"
                />
                <UBadge
                  v-if="
                    route.query.location
                      ?.toString()
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (letter) => letter.toUpperCase()) ===
                    location.name
                  "
                  size="xl"
                  class="absolute top-2 right-2 z-[3] rounded-full bg-neutral-900 p-0.5"
                >
                  <UIcon
                    name="i-lucide-circle-check"
                    class="size-5 shrink-0 text-yellow-400"
                  />
                </UBadge>
                <div
                  class="relative z-[2] flex h-full items-center justify-center text-center"
                >
                  <div class="relative flex flex-col items-center duration-300">
                    <h2
                      class="text-2xl font-extrabold drop-shadow duration-300 group-hover:text-yellow-400"
                    >
                      {{ location.name }}
                    </h2>
                    <p class="text-lg font-bold text-yellow-400 uppercase">
                      {{ location.content.town }}
                    </p>
                  </div>
                </div>
              </LazyUCard>
              <LazyUCard
                v-for="i in 3"
                v-if="loading || isLoading"
                :key="i"
                :ui="{
                  root: 'overflow-hidden aspect-[2/1] sm:aspect-[3/1] md:aspect-[2/1]',
                  body: 'relative isolate w-full h-full',
                }"
              >
                <div
                  class="relative z-[2] flex h-full items-center justify-center text-center"
                >
                  <div class="relative flex w-full flex-col items-center">
                    <USkeleton class="h-8 w-2/3" />
                    <USkeleton class="mt-2 h-4 w-1/2" />
                  </div>
                </div>
              </LazyUCard>
            </div>
          </div>

          <!-- Which Activity Do You Want To Book? -->
          <div
            v-if="groupBooking === false && selectedLocationName !== null"
            class="space-y-6 text-center"
          >
            <h2 class="text-xl font-bold text-yellow-400 drop-shadow-lg">
              Which Activity Do You Want To Book?
            </h2>
            <div
              :class="[
                'grid grid-cols-1 gap-4 sm:grid-cols-2',
                filteredActivities.length === 1 ? 'sm:grid-cols-1' : '',
                filteredActivities.length === 2 ? 'sm:grid-cols-2' : '',
                filteredActivities.length === 3
                  ? 'sm:grid-cols-2 lg:grid-cols-3'
                  : '',
                filteredActivities.length >= 4
                  ? 'sm:grid-cols-2 lg:grid-cols-4'
                  : '',
              ]"
            >
              <LazyUCard
                v-for="(activity, index) in filteredActivities"
                v-if="!loading && !isLoading"
                :key="index"
                :ui="{
                  root: 'overflow-hidden group hover:ring-yellow-400 hover:ring-2 aspect-[2/1] sm:aspect-[3/1] md:aspect-[2/1] duration-300 cursor-pointer bg-black',
                  body: 'relative isolate w-full h-full',
                }"
                @click="
                  selectedLocationActivity(selectedLocationName, activity.slug)
                "
              >
                <NuxtImg
                  provider="storyblok"
                  :modifiers="{ smart: true }"
                  class="absolute inset-0 z-[0] h-full w-full object-cover object-center opacity-60 duration-300 group-hover:scale-105 group-hover:opacity-100"
                  sizes="xs:100svw sm:100svw md:100svw lg:35svw xl:35svw xxl:25svw"
                  :src="activity.content?.feature_image?.filename ?? ''"
                  :alt="activity.content?.feature_image?.alt ?? ''"
                  loading="lazy"
                  placeholder
                />
                <div
                  class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/75 to-neutral-950 opacity-90 duration-300 group-hover:opacity-100"
                />
                <div
                  class="relative z-[2] flex h-full items-center justify-center text-center"
                >
                  <div class="relative flex flex-col items-center duration-300">
                    <h2
                      class="text-2xl font-extrabold drop-shadow duration-300 group-hover:text-yellow-400"
                    >
                      {{ activity.name }}
                    </h2>
                  </div>
                </div>
              </LazyUCard>
              <LazyUCard
                v-for="i in 4"
                v-if="loading || isLoading"
                :key="i"
                :ui="{
                  root: 'overflow-hidden aspect-[2/1] sm:aspect-[3/1] md:aspect-[2/1]',
                  body: 'relative isolate w-full h-full',
                }"
              >
                <div
                  class="relative z-[2] flex h-full items-center justify-center text-center"
                >
                  <div class="relative flex w-full flex-col items-center">
                    <USkeleton class="h-8 w-2/3" />
                  </div>
                </div>
              </LazyUCard>
            </div>
          </div>
        </div>
        <template v-if="groupBooking !== null" #footer>
          <div class="flex justify-end">
            <UButton
              v-if="groupBooking === true && selectedGroup === null"
              variant="subtle"
              color="primary"
              class="cursor-pointer"
              @click="groupBooking = null"
            >
              Go Back
            </UButton>
            <UButton
              v-if="groupBooking === true && selectedGroup !== null"
              variant="subtle"
              color="primary"
              class="cursor-pointer"
              @click="selectedGroup = null"
            >
              Go Back
            </UButton>
            <UButton
              v-if="groupBooking === false && selectedLocationName === null"
              variant="subtle"
              color="primary"
              class="cursor-pointer"
              @click="groupBooking = null"
            >
              Go Back
            </UButton>
            <UButton
              v-if="groupBooking === false && selectedLocationName !== null"
              variant="subtle"
              color="primary"
              class="cursor-pointer"
              @click="selectedLocationName = null"
            >
              Go Back
            </UButton>
          </div>
        </template>
      </UCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  GroupStoryblok,
  ActivityStoryblok,
  LocationStoryblok,
} from '~/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/vue';

const route = useRoute();
const { fetchGroups, fetchActivities, isLoading } = useStoryblokClient();
const { slugify } = useSlugify();

const loading = ref(false);
const groupBooking = ref<boolean | null>(null);
const groups = ref<ISbStoryData<GroupStoryblok>[]>([]);
const selectedGroup = ref<ISbStoryData<GroupStoryblok> | null>(null);
const activities = ref<ISbStoryData<ActivityStoryblok>[]>([]);
const locations = ref<ISbStoryData<LocationStoryblok>[]>([]);
const selectedLocationName = ref<string | null>(null);
const filteredActivities = ref<ISbStoryData<ActivityStoryblok>[]>([]);

const loadGroups = async () => {
  loading.value = true;
  try {
    const [stories] = await Promise.all([fetchGroups()]);
    groups.value = stories as unknown as ISbStoryData<GroupStoryblok>[];
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

watchEffect(async () => {
  if (groupBooking.value === true && groups.value.length === 0) {
    await loadGroups();
  }
});

const selectGroup = (group: ISbStoryData<GroupStoryblok>) => {
  selectedGroup.value = group;
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const selectGroupLocation = (groupSlug: string, locationName: string) => {
  if (typeof groupSlug === 'string' && groupSlug.includes('corporate')) {
    navigateTo(`/book/corporates?location=${slugify(locationName)}`);
  } else {
    navigateTo(`/${groupSlug}?tab=packages&location=${slugify(locationName)}`);
  }
};

const loadActivities = async () => {
  loading.value = true;
  try {
    const [stories] = await Promise.all([fetchActivities()]);
    activities.value = stories as unknown as ISbStoryData<ActivityStoryblok>[];

    // Map and deduplicate locations (for objects)
    locations.value = Array.from(
      new Map(
        activities.value
          .map((activity) => activity.content.locations)
          .flat()
          .filter(Boolean)
          .map((location) => [location.id, location])
      ).values()
    );

    // Remove Corporate Activities
    activities.value = activities.value
      .filter(
        (activity) => !activity.content.activity_types.includes('corporate')
      )
      .sort((a, b) => {
        const typeOrder =
          Number(b.content?.activity_types.includes('corporate')) -
          Number(a.content?.activity_types.includes('corporate'));
        if (typeOrder !== 0) return typeOrder;

        const priceA = Number(a.content?.products?.[0]?.price ?? 0);
        const priceB = Number(b.content?.products?.[0]?.price ?? 0);
        const priceOrder = priceB - priceA;
        if (priceOrder !== 0) return priceOrder;

        return a.name.localeCompare(b.name);
      });
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

watchEffect(async () => {
  if (groupBooking.value === false && activities.value.length === 0) {
    await loadActivities();
  }
});

const selectedLocation = async (locationName: string) => {
  selectedLocationName.value = locationName;

  filteredActivities.value = activities.value.filter((activity) =>
    activity.content.locations.some(
      (location: ISbStoryData<LocationStoryblok>) =>
        location.name === selectedLocationName.value
    )
  );
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const selectedLocationActivity = (
  locationName: string,
  activitySlug: string
) => {
  navigateTo(
    `/activities/${activitySlug}?tab=availability&location=${slugify(locationName)}`
  );
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};
</script>
